package org.teddapp.project.ui.notes

/** Un texte et sa sélection, après une mise en forme. */
internal data class MdEdit(val text: String, val start: Int, val end: Int = start)

/** Ce qu'une ligne peut être : un titre, un élément de liste, une citation. */
internal enum class MdLine { H1, H2, H3, Bullet, Numbered, Task, Quote }

/**
 * La mise en forme d'une note, en Markdown : des signes ajoutés ou retirés autour de la
 * sélection, ou en tête des lignes qu'elle touche.
 *
 * Tout est fonction pure d'un texte et d'une sélection : la barre d'outils, les
 * raccourcis clavier et la touche Entrée passent par ici, et les tests aussi.
 */
internal object MarkdownEdits {

    // Une ligne ne porte qu'un préfixe de chaque famille : poser un titre remplace le
    // titre, poser une liste remplace la liste.
    private val heading = Regex("^#{1,6} ")
    private val listItem = Regex("^(?:[-*+] \\[[ xX]\\] |[-*+] |\\d+[.)] )")
    private val quote = Regex("^> ?")
    private val task = Regex("^[-*+] \\[[ xX]\\] ")
    private val bullet = Regex("^[-*+] (?!\\[[ xX]\\] )")
    private val numbered = Regex("^\\d+[.)] ")

    /** Un élément de liste ou de citation qu'Entrée prolonge. */
    private val continuable = Regex("^([ \\t]*)([-*+] \\[[ xX]\\] |[-*+] |(\\d+)([.)]) |> ?)(.*)$")

    /**
     * Entoure la sélection de [marker] (`**`, `_`, `~~`, `` ` ``), ou l'en libère si elle
     * l'est déjà. Sans sélection, le mot sous le curseur ; hors d'un mot, une paire vide
     * avec le curseur au milieu. Une sélection sur plusieurs lignes est mise en forme ligne
     * à ligne : le Markdown ne fait pas traverser un gras d'un paragraphe à l'autre.
     */
    fun toggleWrap(text: String, start: Int, end: Int, marker: String): MdEdit {
        val s = minOf(start, end)
        val e = maxOf(start, end)
        val m = marker.length

        if (s == e) {
            val (ws, we) = wordAround(text, s)
            if (ws == we) return MdEdit(text.substring(0, s) + marker + marker + text.substring(s), s + m)
            val r = toggleWrap(text, ws, we, marker)
            // Le curseur reste où il était dans le mot.
            return MdEdit(r.text, if (r.text.length > text.length) s + m else s - m)
        }

        // La sélection est déjà entourée : on retire les signes de part et d'autre.
        if (s >= m && e + m <= text.length &&
            text.regionMatches(s - m, marker, 0, m) && text.regionMatches(e, marker, 0, m)
        ) {
            val t = text.substring(0, s - m) + text.substring(s, e) + text.substring(e + m)
            return MdEdit(t, s - m, e - m)
        }

        val segments = segments(text, s, e)
        if (segments.isEmpty()) return MdEdit(text, s, e)
        val wrapped = segments.all { (a, b) ->
            b - a >= 2 * m && text.regionMatches(a, marker, 0, m) && text.regionMatches(b - m, marker, 0, m)
        }
        val out = StringBuilder()
        var last = 0
        for ((a, b) in segments) {
            out.append(text, last, a)
            if (wrapped) out.append(text, a + m, b - m) else out.append(marker).append(text, a, b).append(marker)
            last = b
        }
        out.append(text, last, text.length)
        val t = out.toString()
        if (segments.size == 1) {
            // Une seule ligne : la sélection reste sur le texte, entre les signes. Un second
            // appui la retrouve ainsi entourée, et retire la mise en forme.
            val (a, b) = segments[0]
            return if (wrapped) MdEdit(t, a, b - 2 * m) else MdEdit(t, a + m, b + m)
        }
        return MdEdit(t, s, e + (t.length - text.length))
    }

    /**
     * Pose [kind] en tête de chaque ligne touchée par la sélection, ou l'en retire si toutes
     * le portent déjà. L'indentation est gardée : une sous-liste reste une sous-liste.
     */
    fun toggleLine(text: String, start: Int, end: Int, kind: MdLine): MdEdit {
        val s = minOf(start, end)
        var e = maxOf(start, end)
        // Une sélection qui finit au tout début d'une ligne ne la touche pas vraiment.
        if (e > s && text[e - 1] == '\n') e--
        val ls = lineStart(text, s)
        val le = lineEnd(text, e)
        val lines = text.substring(ls, le).split('\n')

        val family = when (kind) {
            MdLine.H1, MdLine.H2, MdLine.H3 -> heading
            MdLine.Bullet, MdLine.Numbered, MdLine.Task -> listItem
            MdLine.Quote -> quote
        }
        val has: (String) -> Boolean = { bare ->
            when (kind) {
                MdLine.H1 -> bare.startsWith("# ")
                MdLine.H2 -> bare.startsWith("## ")
                MdLine.H3 -> bare.startsWith("### ")
                MdLine.Bullet -> bullet.containsMatchIn(bare)
                MdLine.Numbered -> numbered.containsMatchIn(bare)
                MdLine.Task -> task.containsMatchIn(bare)
                MdLine.Quote -> bare.startsWith(">")
            }
        }
        val many = lines.size > 1
        val targets = lines.filter { !many || it.isNotBlank() }
        val remove = targets.isNotEmpty() && targets.all { has(it.trimStart(' ', '\t')) }

        var n = 0
        val changed = lines.map { line ->
            if (many && line.isBlank()) return@map line
            val indent = line.takeWhile { it == ' ' || it == '\t' }
            val rest = family.replaceFirst(line.substring(indent.length), "")
            if (remove) indent + rest else indent + prefix(kind, ++n) + rest
        }
        val block = changed.joinToString("\n")
        val t = text.substring(0, ls) + block + text.substring(le)
        if (!many && s == maxOf(start, end)) {
            // Un curseur dans une ligne suit le texte qu'il désignait.
            val delta = changed[0].length - lines[0].length
            return MdEdit(t, (s + delta).coerceIn(ls, ls + block.length))
        }
        return MdEdit(t, ls, ls + block.length)
    }

    /**
     * Un lien. Sur un texte sélectionné, il devient le libellé et l'adresse est
     * sélectionnée, prête à être collée ; sur une adresse, elle devient la cible.
     */
    fun link(text: String, start: Int, end: Int): MdEdit {
        val s = minOf(start, end)
        val e = maxOf(start, end)
        val sel = text.substring(s, e)
        val before = text.substring(0, s)
        val after = text.substring(e)
        if (sel.isEmpty()) return MdEdit("$before[](https://)$after", s + 1)
        if (sel.startsWith("http://") || sel.startsWith("https://")) {
            return MdEdit("$before[]($sel)$after", s + 1)
        }
        val url = s + sel.length + 3
        return MdEdit("$before[$sel](https://)$after", url, url + "https://".length)
    }

    /**
     * Du code. Dans une ligne, un code en ligne ; sur plusieurs lignes, ou sur une ligne
     * vide, un bloc entre deux clôtures.
     */
    fun code(text: String, start: Int, end: Int): MdEdit {
        val s = minOf(start, end)
        val e = maxOf(start, end)
        val ls = lineStart(text, s)
        val le = lineEnd(text, e)
        val block = text.substring(s, e).contains('\n') || text.substring(ls, le).isBlank()
        if (!block) return toggleWrap(text, s, e, "`")
        val inner = text.substring(ls, le)
        val t = text.substring(0, ls) + "```\n" + inner + "\n```" + text.substring(le)
        return MdEdit(t, ls + 4, ls + 4 + inner.length)
    }

    /** Un séparateur, isolé par des lignes vides : collé sous un texte, il en ferait un titre. */
    fun rule(text: String, cursor: Int): MdEdit {
        val c = cursor.coerceIn(0, text.length)
        val head = text.substring(0, c)
        val before = when {
            c == 0 || head.endsWith("\n\n") -> ""
            head.endsWith("\n") -> "\n"
            else -> "\n\n"
        }
        val after = if (text.startsWith("\n", c)) "\n" else "\n\n"
        val inserted = "$before---$after"
        return MdEdit(head + inserted + text.substring(c), c + inserted.length)
    }

    /** La ligne qui place un dessin dans le texte. */
    fun inkRef(id: String) = "![dessin](ink:$id)"

    /** Un dessin cité seul sur sa ligne : son identifiant, ou null. */
    private val inkLine = Regex("^!\\[[^\\]]*\\]\\(ink:([A-Za-z0-9_-]+)\\)\\s*$")

    fun inkIdOf(line: String): String? = inkLine.find(line)?.groupValues?.get(1)

    /** La ligne qui place une image (un fichier joint) dans le texte. */
    fun fileRef(id: String) = "![](file:$id)"

    private val fileLine = Regex("^!\\[[^\\]]*\\]\\(file:([A-Za-z0-9_-]+)\\)\\s*$")

    /** Une image citée seule sur sa ligne : son identifiant, ou null. */
    fun fileIdOf(line: String): String? = fileLine.find(line)?.groupValues?.get(1)

    /** Retire les lignes qui citent l'image [id]. */
    fun removeFile(text: String, id: String): String =
        text.split('\n').filterNot { fileIdOf(it) == id }.joinToString("\n")

    /** Pose [line] seule sur sa ligne en [cursor], et met le curseur juste après. */
    fun block(text: String, cursor: Int, line: String): MdEdit {
        val c = cursor.coerceIn(0, text.length)
        val head = text.substring(0, c)
        val before = if (c == 0 || head.endsWith("\n")) "" else "\n"
        val after = if (text.startsWith("\n", c)) "" else "\n"
        val inserted = before + line + after
        return MdEdit(head + inserted + text.substring(c), c + inserted.length)
    }

    /**
     * Pose [text] juste sous la ligne du dessin [id], ou à sa place si [replace]. Sans
     * ligne pour ce dessin, le texte va à la fin.
     */
    fun besideInk(text: String, id: String, inserted: String, replace: Boolean): String {
        val lines = text.split('\n').toMutableList()
        val at = lines.indexOfFirst { inkIdOf(it) == id }
        if (at < 0) return if (text.isEmpty()) inserted else text.trimEnd('\n') + "\n" + inserted
        if (replace) lines[at] = inserted else lines.add(at + 1, inserted)
        return lines.joinToString("\n")
    }

    /** Retire les lignes qui citent le dessin [id]. */
    fun removeInk(text: String, id: String): String =
        text.split('\n').filterNot { inkIdOf(it) == id }.joinToString("\n")

    /**
     * Après un retour à la ligne tapé en [cursor] : un élément de liste appelle le suivant
     * (numéro suivant, case vide), un élément vide fait sortir de la liste. Null si la
     * ligne précédente n'est ni une liste ni une citation.
     */
    fun continueList(text: String, cursor: Int): MdEdit? {
        if (cursor <= 0 || cursor > text.length || text[cursor - 1] != '\n') return null
        val prevStart = lineStart(text, cursor - 1)
        val prev = text.substring(prevStart, cursor - 1)
        val match = continuable.find(prev) ?: return null
        val indent = match.groupValues[1]
        val marker = match.groupValues[2]
        val number = match.groupValues[3]
        val delim = match.groupValues[4]
        val content = match.groupValues[5]

        if (content.isBlank()) {
            // Entrée sur un élément vide : on sort de la liste. La ligne vide reste, sans
            // elle le paragraphe suivant serait rattaché au dernier élément.
            val t = text.substring(0, prevStart) + text.substring(cursor - 1)
            return MdEdit(t, prevStart + 1)
        }
        val next = when {
            number.isNotEmpty() -> "${number.toLong() + 1}$delim "
            task.containsMatchIn(marker) -> "${marker[0]} [ ] "
            marker.startsWith(">") -> "> "
            else -> marker
        }
        val insert = indent + next
        return MdEdit(text.substring(0, cursor) + insert + text.substring(cursor), cursor + insert.length)
    }

    /** Coche ou décoche la case `[ ]` qui commence en [offset]. */
    fun toggleTaskAt(text: String, offset: Int): String {
        if (offset < 0 || offset + 3 > text.length) return text
        return when (text.substring(offset, offset + 3)) {
            "[ ]" -> text.replaceRange(offset, offset + 3, "[x]")
            "[x]", "[X]" -> text.replaceRange(offset, offset + 3, "[ ]")
            else -> text
        }
    }

    /**
     * Le texte sans ses signes : pour un aperçu ou un compte de mots, une case, une puce
     * ou des astérisques ne sont pas des mots.
     */
    // RegexOption.MULTILINE plutôt que (?m) : le navigateur refuse les options en ligne.
    fun plain(text: String): String = text
        .replace(Regex("^[ \\t]*(?:#{1,6} |>+ ?|[-*+] \\[[ xX]\\] |[-*+] |\\d+[.)] )", RegexOption.MULTILINE), "")
        // Une image ou un dessin n'est pas du texte ; un lien garde son libellé.
        .replace(Regex("!\\[[^\\]]*\\]\\([^)]*\\)"), "")
        .replace(Regex("\\[([^\\]]*)\\]\\([^)]*\\)"), "$1")
        // Un lien vers une note garde son titre.
        .replace(Regex("\\[\\[([^\\[\\]\\n]+)\\]\\]"), "$1")
        .replace(Regex("^(?:```.*|---+)$", RegexOption.MULTILINE), "")
        .replace(Regex("[*_~`]+|=="), "")
        .replace(Regex("\\s+"), " ")
        .trim()

    private fun prefix(kind: MdLine, n: Int): String = when (kind) {
        MdLine.H1 -> "# "
        MdLine.H2 -> "## "
        MdLine.H3 -> "### "
        MdLine.Bullet -> "- "
        MdLine.Numbered -> "$n. "
        MdLine.Task -> "- [ ] "
        MdLine.Quote -> "> "
    }

    private fun lineStart(text: String, at: Int): Int = text.lastIndexOf('\n', (at - 1).coerceAtLeast(-1)) + 1

    private fun lineEnd(text: String, at: Int): Int = text.indexOf('\n', at).let { if (it < 0) text.length else it }

    private fun isWord(c: Char) = c.isLetterOrDigit() || c == '\'' || c == '’' || c == '-'

    private fun wordAround(text: String, at: Int): Pair<Int, Int> {
        var a = at
        var b = at
        while (a > 0 && isWord(text[a - 1])) a--
        while (b < text.length && isWord(text[b])) b++
        return a to b
    }

    /** Les morceaux non vides de [s, e[, ligne par ligne, débarrassés des espaces de bord. */
    private fun segments(text: String, s: Int, e: Int): List<Pair<Int, Int>> {
        val acc = mutableListOf<Pair<Int, Int>>()
        var a = s
        while (a <= e) {
            val nl = text.indexOf('\n', a).let { if (it < 0 || it > e) e else it }
            var x = a
            var y = nl
            while (x < y && text[x].isWhitespace()) x++
            while (y > x && text[y - 1].isWhitespace()) y--
            if (x < y) acc += x to y
            a = nl + 1
        }
        return acc
    }
}
